import * as React from "react";
import { Box, FormControlLabel, Radio } from "@mui/material";
import { getAllDashboardData, getAllOwners, statementRentalIncomeReport } from "state/apis";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import "./index.css";
import { TextField, Grid } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import moment from "moment";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Autocomplete from "@mui/material/Autocomplete";
import { formatAmount } from "utils/constants";

const RentalReport = () => {
    const propertyName = useSelector((state) => state.global.propertyName);
    const [isLoading, setIsLoading] = React.useState(false);
    const [data, setData] = React.useState({});
    const [owners, setOwners] = React.useState([]);
    const start = moment().startOf("month").format("YYYY-MM-DD");
    const end = moment().endOf("month").format("YYYY-MM-DD");
    const currentYear = moment().format("YYYY-MM");
    const [monthChecked, setMonthChecked] = React.useState(true);
    const [dateChecked, setDateChecked] = React.useState(false);
    const [allProperty, setAllProperty] = React.useState([]);

    const [filter, setFilter] = React.useState({
        startDate: start,
        endDate: end,
        monthValue: currentYear,
        ownerId: null,
        propertyId: null,
    });

    React.useEffect(() => {
        setIsLoading(true);
        fetchOwners();
        fetchProperty();
        fetchData();
        setIsLoading(false);
    }, []);

    React.useEffect(() => {
        fetchData();
    }, [filter.ownerId, filter.startDate, filter.endDate, filter.propertyId]);

    const fetchData = async () => {
        try {
            const res = await statementRentalIncomeReport({
                ownerId: filter.ownerId,
                startDate: filter.startDate,
                endDate: filter.endDate,
                propertyId: filter.propertyId,
            });
            setData(res.data);
            setIsLoading(false);
        } catch (error) {
            console.error("Error fetching data:", error);
            setIsLoading(false);
        }
    };

    const fetchProperty = async () => {
        const res = await getAllDashboardData();
        setAllProperty(res?.data?.properties || []);
    };

    const fetchOwners = async () => {
        try {
            const res = await getAllOwners(100000, 0);
            setOwners(res?.owners);
        } catch (error) {
            console.error("Error fetching owners:", error);
            toast.error("Failed to fetch owners. Please try again.");
        }
    };

    const handleOwnerChange = (newValue) => {
        setFilter((prevData) => ({
            ...prevData,
            ownerId: newValue ? newValue.id : null,
        }));
    };
    const handlePropertyChange = (newValue) => {
        setFilter((prevData) => ({
            ...prevData,
            propertyId: newValue ? newValue.id : null,
        }));
    };

    const handleMonthChange = (event) => {
        const isChecked = event.target.checked;
        setMonthChecked(isChecked);
        if (isChecked) {
            setDateChecked(false);
        }
    };

    const handleDateChange = (event) => {
        const isChecked = event.target.checked;
        setDateChecked(isChecked);
        if (isChecked) {
            setMonthChecked(false);
        }
    };

    const handleFrom = (date) => {
        const data = date;
        const formattedDate = data.format("YYYY-MM-DD");

        setFilter((prevData) => ({
            ...prevData,
            startDate: formattedDate,
        }));
    };
    const handleTo = (date) => {
        const data = date;
        const formattedDate = data.format("YYYY-MM-DD");

        setFilter((prevData) => ({
            ...prevData,
            endDate: formattedDate,
        }));
    };
    const handleMonth = (date) => {
        if (!date) return;
        const selectedDate = dayjs(date);

        const startDate = selectedDate.startOf("month").format("YYYY-MM-DD");
        const endDate = selectedDate.endOf("month").format("YYYY-MM-DD");

        setFilter((prevData) => ({
            ...prevData,
            startDate: startDate,
            endDate: endDate,
            monthValue: date ? date.format("YYYY-MM") : null,
        }));
    };

    const reportRef = React.useRef();

    const handlePrint = () => {
        const input = reportRef.current;

        html2canvas(input, { scale: 2 }).then((canvas) => {
            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF("p", "mm", "a4");

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();

            const margin = 10; // Add margin (padding)

            // Get the image height and scale it proportionally
            const imgProps = pdf.getImageProperties(imgData);
            const imgHeight = (imgProps.height * (pdfWidth - 2 * margin)) / imgProps.width;

            let heightLeft = imgHeight;
            let position = margin;

            pdf.addImage(imgData, "PNG", margin, position, pdfWidth - 2 * margin, imgHeight);
            heightLeft -= pdfHeight - 2 * margin;

            while (heightLeft > 0) {
                position = heightLeft - imgHeight + margin;
                pdf.addPage();
                pdf.addImage(imgData, "PNG", margin, position, pdfWidth - 2 * margin, imgHeight);
                heightLeft -= pdfHeight - 2 * margin;
            }

            pdf.save(`${propertyName}-income-and-expense.pdf`);
        });
    };

    const handleDownloadCSV = () => {
        const table = document.querySelector("table");
        let csvContent = "";

        // Loop through table rows
        for (const row of table.rows) {
            const rowData = [];
            for (const cell of row.cells) {
                const cellText = cell.textContent.trim().replace(/"/g, '""');
                rowData.push(`"${cellText}"`);
            }
            csvContent += rowData.join(",") + "\n";
        }

        // Create Blob and trigger download
        const blob = new Blob([csvContent], { type: "text/csv" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${propertyName}-income-and-expense.csv`;
        link.click();
        URL.revokeObjectURL(link.href); // Cleanup
    };

    let totalAmount = 0;

    return (
        <Box m="1.5rem 2.5rem" mb={5}>
            <Box display="flex" justifyContent="flex-end" pt={0} paddingLeft={0}>
                <Grid container spacing={2} p={1} alignItems="center">
                    {/* First Row - Dropdowns */}
                    <Grid container item xs={12} spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                            <Autocomplete
                                disablePortal
                                id="combo-box-owner"
                                options={owners}
                                getOptionLabel={(option) => option.name}
                                value={
                                    filter.ownerId
                                        ? owners.find((owner) => owner.id === filter.ownerId)
                                        : null
                                }
                                sx={{
                                    width: "100%",
                                    "& .MuiOutlinedInput-root": {
                                        "&.Mui-focused fieldset": {
                                            borderColor: "primary.main",
                                        },
                                    },
                                    "& .MuiInputLabel-root": {
                                        "&.Mui-focused": {
                                            color: "primary.main",
                                        },
                                    },
                                }}
                                onChange={(event, newValue) => handleOwnerChange(newValue)}
                                renderInput={(params) => (
                                    <TextField {...params} fullWidth label="Select Owner" />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <Autocomplete
                                disablePortal
                                id="combo-box-property"
                                options={allProperty}
                                getOptionLabel={(option) => option.propertyName}
                                value={
                                    filter.propertyId
                                        ? allProperty.find(
                                              (property) => property.id === filter.propertyId
                                          )
                                        : null
                                }
                                sx={{
                                    width: "100%",
                                    "& .MuiOutlinedInput-root": {
                                        "&.Mui-focused fieldset": {
                                            borderColor: "primary.main",
                                        },
                                    },
                                    "& .MuiInputLabel-root": {
                                        "&.Mui-focused": {
                                            color: "primary.main",
                                        },
                                    },
                                }}
                                onChange={(event, newValue) => handlePropertyChange(newValue)}
                                renderInput={(params) => (
                                    <TextField {...params} fullWidth label="Select Property" />
                                )}
                            />
                        </Grid>
                    </Grid>

                    <Grid container item xs={12} spacing={2} alignItems="center">
                        <Grid item>
                            <FormControlLabel
                                value="month"
                                control={
                                    <Radio
                                        checked={monthChecked}
                                        onChange={handleMonthChange}
                                        color="primary"
                                    />
                                }
                                label="Month"
                            />
                        </Grid>

                        <Grid item>
                            <FormControlLabel
                                value="date"
                                control={
                                    <Radio
                                        checked={dateChecked}
                                        onChange={handleDateChange}
                                        color="primary"
                                    />
                                }
                                label="Date Wise"
                            />
                        </Grid>

                        {/* Date Pickers - Conditional Rendering */}
                        {dateChecked && (
                            <>
                                <Grid item xs={12} sm={6} md={3}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            label="From"
                                            name="contractFrom"
                                            onChange={handleFrom}
                                            value={
                                                filter.startDate ? dayjs(filter.startDate) : null
                                            }
                                            format="DD/MM/YYYY"
                                            sx={{
                                                width: "100%",
                                                "& .MuiOutlinedInput-root": {
                                                    "&.Mui-focused fieldset": {
                                                        borderColor: "primary.main",
                                                    },
                                                },
                                                "& .MuiInputLabel-root": {
                                                    "&.Mui-focused": { color: "primary.main" },
                                                },
                                            }}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            label="To"
                                            name="contractTo"
                                            onChange={handleTo}
                                            value={filter.endDate ? dayjs(filter.endDate) : null}
                                            format="DD/MM/YYYY"
                                            sx={{
                                                width: "100%",
                                                "& .MuiOutlinedInput-root": {
                                                    "&.Mui-focused fieldset": {
                                                        borderColor: "primary.main",
                                                    },
                                                },
                                                "& .MuiInputLabel-root": {
                                                    "&.Mui-focused": { color: "primary.main" },
                                                },
                                            }}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                            </>
                        )}

                        {monthChecked && (
                            <Grid item xs={12} sm={6} md={3}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="Month"
                                        views={["month", "year"]}
                                        name="month"
                                        onChange={handleMonth}
                                        value={filter.monthValue ? dayjs(filter.monthValue) : null}
                                        sx={{
                                            width: "100%",
                                            "& .MuiOutlinedInput-root": {
                                                "&.Mui-focused fieldset": {
                                                    borderColor: "primary.main",
                                                },
                                            },
                                            "& .MuiInputLabel-root": {
                                                "&.Mui-focused": { color: "primary.main" },
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                        )}

                        <Grid item xs></Grid>
                        <Grid item xs={12} sm={4} md={2} container justifyContent="flex-end">
                            <button className="download-btn" onClick={handlePrint}>
                                Download PDF
                            </button>
                        </Grid>
                        <Grid item xs="auto">
                            <button className="download-btn" onClick={handleDownloadCSV}>
                                Download CSV
                            </button>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>

            <div className="report-container">
                <div ref={reportRef}>
                    <table border="1" cellPadding="5" cellSpacing="0">
                        <thead>
                            <tr>
                                <th colSpan={7} style={{ textAlign: "center" }}>
                                    STATEMENT OF RENTAL INCOME FOR THE PERIOD
                                </th>
                            </tr>
                            <tr>
                                <th colSpan={7} style={{ textAlign: "center" }}>
                                    {`${moment(filter.startDate).format("DD/MM/YYYY")} to ${moment(
                                        filter.endDate
                                    ).format("DD/MM/YYYY")}`}
                                </th>
                            </tr>
                            <tr>
                                <th colSpan={7} style={{ textAlign: "end" }}>
                                    {moment().format("DD/MM/YYYY")}
                                </th>
                            </tr>
                            <tr>
                                <th style={{ textAlign: "center" }}>Sl. NO</th>
                                <th style={{ textAlign: "center" }}>PROPERTY</th>
                                <th style={{ textAlign: "center" }}>UNIT</th>
                                <th style={{ textAlign: "center" }}>RENT AMOUNT RECEIVED</th>
                                <th style={{ textAlign: "center" }}>On</th>
                                <th style={{ textAlign: "center" }}>BY</th>
                                <th style={{ textAlign: "center" }}>RV No</th>
                            </tr>
                        </thead>

                        <tbody>
                            {data && data.length ? (
                                <>
                                    {data.map((val, index) => {
                                        totalAmount += parseFloat(val.amount);

                                        return (
                                            <tr key={index}>
                                                <td>{index + 1}</td>
                                                <td>{val?.property?.propertyName}</td>
                                                <td>{val?.unit?.unitNumber}</td>
                                                <td style={{ textAlign: "right" }}>
                                                    {formatAmount(Number(val?.amount))}
                                                </td>
                                                <td>{moment(val?.date).format("DD/MM/YYYY")}</td>
                                                <td>{val?.transactionType}</td>
                                                <td>{val?.bankRefNumber}</td>
                                            </tr>
                                        );
                                    })}
                                </>
                            ) : (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: "center" }}>
                                        No data found
                                    </td>
                                </tr>
                            )}
                            <tr>
                                <td></td>
                                <td></td>
                                <td style={{ fontWeight: "bold" }}>Total:</td>
                                <td style={{ textAlign: "right" }}>
                                    {formatAmount(Number(totalAmount))}
                                </td>
                                <td></td>
                                <td></td>
                                <td></td>
                            </tr>
                            <tr>
                                <td colSpan={7}></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </Box>
    );
};

export default RentalReport;
